/**
 * This module takes a data structure, beit a CodeQuery, SearchResult,
 * or CodeChange and returns a data structure that works with the backend
 * API (see https://codametrix.atlassian.net/wiki/spaces/~381675221/pages/2262925413/Update+API)
 */
import * as commonEnums from '../../enums';
const {
  ChargeSessionCodeType,
  ChargeSessionCodeProvenance,
  ChargeSessionType,
  CrudOperations
} = commonEnums;

const BASE_DX = {
  evidence: [],
  type: null,
  used: true,
  confidence: 1.0,
  threashold: 0.0,
  automate: false,
  diagnosis_code: '',
  sequence_number: 0
};

const BASE_MOD = {
  evidence: [],
  used: true,
  confidence: 1.0,
  threashold: 0.0,
  automate: false,
  modifier_code: '',
  sequence_number: 0
};

const BASE_PROC: InvoicesAPI.ProcedureCode = {
  evidence: [],
  confidence: 1.0,
  threashold: 0.0,
  automate: false,
  used: true,
  procedure_name: '',
  procedure_id: null,
  procedure_code: '',
  type: ChargeSessionCodeProvenance.CPT.toLowerCase(),
  units: 1.0
};

const BASE_TECH: InvoicesAPI.TechnicalCode = {
  code: '',
  provenance: ChargeSessionCodeProvenance.TECH.toLowerCase(),
  units: 1.0
};

const codeMaker = {
  icd: (stub: Partial<InvoicesAPI.DiagnosticCode>) => {
    return {
      ...BASE_DX,
      ...stub
    };
  },
  modifier: (stub: Partial<InvoicesAPI.Modifier>) => {
    return { ...BASE_MOD, ...stub };
  },
  procedure: (stub: Partial<InvoicesAPI.ProcedureCode>) => {
    return {
      ...BASE_PROC,
      ...stub
    };
  },
  technical: (stub: Partial<InvoicesAPI.TechnicalCode>) => {
    return {
      ...BASE_TECH,
      ...stub
    };
  }
};

const _isProvenance = (provenance: string) => (
  searchSelection: CMxCommonApp.CodeInfo
) => {
  const codeType = `${searchSelection.codeType}`.toUpperCase();
  return codeType === `${provenance}`.toUpperCase();
};

const _isChange = (...codeTypes: string[]) => {
  const codeSet = new Set(codeTypes.map(codeType => codeType.toUpperCase()));

  return (codeChange: Accelerate.CodeChange) => {
    const {
      codes: [code]
    } = codeChange;
    return codeSet.has(`${code.codeType}`.toUpperCase());
  };
};
// -----------------------------
// Predicate functions
// -----------------------------
const isSelectionICD = _isProvenance(ChargeSessionCodeProvenance.ICD);
const isSelectionModifier = _isProvenance(ChargeSessionCodeProvenance.MODIFIER);
const isSelectionCPT = _isProvenance(ChargeSessionCodeProvenance.CPT);
const isSelectionTech = _isProvenance(ChargeSessionType.TECHNICAL);

const codePredicates = {
  isSelectionICD,
  isSelectionModifier,
  isSelectionCPT,
  isSelectionTech
};

export const isICDChange = _isChange(
  ChargeSessionCodeType.DIAGNOSIS,
  ChargeSessionCodeProvenance.ICD
);
export const isModifierChange = _isChange(ChargeSessionCodeType.MODIFIER);
export const isCPTChange = _isChange(ChargeSessionCodeType.PROCEDURE);
export const isTechChange = _isChange(ChargeSessionCodeProvenance.TECH);

const isProcedure = (searchSelection: CMxCommonApp.CodeInfo) => {
  return isSelectionCPT(searchSelection) || isSelectionTech(searchSelection);
};

const isProceduralUpdate = (codeChange: Accelerate.CodeChange) => {
  const [code] = codeChange.codes;
  return code.codeType === ChargeSessionCodeType.PROCEDURE;
};

// -----------------------------
// Typeguards
// -----------------------------
const isSelection = (
  selection: any
): selection is Accelerate.CodeAddActionPayload => {
  return (
    `codeInfo` in selection ||
    (`codesInfo` in selection && 'context' in selection)
  );
};

const isDxCodes = (codes: any): codes is InvoicesAPI.DiagnosticCode[] => {
  return codes.every(code => `diagnosis_code` in code);
};
const isModCodes = (codes: any): codes is InvoicesAPI.Modifier[] => {
  return codes.every(code => `modifier_code` in code);
};

// -----------------------------
// General helper functions.
// -----------------------------
const getInvoice = (codeChange: Accelerate.CodeActionContextual) => {
  return (codeChange.context.invoices ?? []).find(invoice => {
    return invoice.guid === codeChange.context.chargeSession.invoiceId;
  });
};

const getTransaction = (
  invoice: CMxCommonApp.CodableInvoice,
  transactionGuid: string
): AmplifyAPI.Transaction => {
  return (invoice.transactions || []).find(txn => txn.guid === transactionGuid);
};

const matchProcedure = (
  invoiceProcedure: AmplifyAPI.ProcedureCode,
  updatedCode: CMxCommonApp.ChargeSessionCode
) => {
  if (!invoiceProcedure.procedure_code) return false;

  return updatedCode.code === invoiceProcedure.procedure_code;
};

const matchTech = (
  invoiceProcedure: AmplifyAPI.TechnicalCode,
  updatedCode: CMxCommonApp.ChargeSessionCode
) => {
  if (!invoiceProcedure.technical_code) return false;

  return updatedCode.code === invoiceProcedure.technical_code;
};

export const getTransactionFromChange = (codeChange: Accelerate.CodeChange) => {
  if (codeChange.context.transaction) {
    return codeChange.context.transaction;
  }
  const invoice = getInvoice(codeChange);
  return getTransaction(
    invoice,
    codeChange.context.chargeSession.transactionId
  );
};

const getCodesForUpdate = (codeChange: Accelerate.CodeChange) => {
  //@ts-ignore
  const transaction = codeChange.context.transaction;
  //const transaction = getTransactionFromChange(codeChange);

  const codes = [];

  codeChange.codes.forEach(code => {
    if (
      `${code.provenance}`.toUpperCase() === ChargeSessionCodeProvenance.TECH
    ) {
      if (matchTech(transaction.technical_code, code)) {
        codes.push(transaction.technical_code);
      }
    } else if (code.codeType === ChargeSessionCodeType.PROCEDURE) {
      if (matchProcedure(transaction.procedure, code)) {
        codes.push(transaction.procedure);
      }
    } else if (code.codeType === ChargeSessionCodeType.DIAGNOSIS) {
      transaction.diagnoses.forEach(dx => codes.push(dx));
    } else if (code.codeType === ChargeSessionCodeType.MODIFIER) {
      transaction.modifiers.forEach(mod => codes.push(mod));
    }
  });

  return codes;
};

const isCodeChangeType = (
  selection: any
): selection is Accelerate.CodeChange => {
  return 'codes' in selection;
};

const getOriginalCodes = (
  searchSelection: Accelerate.CodeAddActionPayload | Accelerate.CodeChange,
  isCodeChange: boolean = false
): any[] => {
  const transaction = searchSelection.context.transaction ?? {};

  if (isSelection(searchSelection)) {
    if (isProcedure(searchSelection.codeInfo)) {
      return [];
    } else if (isSelectionICD(searchSelection.codeInfo)) {
      return [...transaction.diagnoses];
    } else if (isSelectionModifier(searchSelection.codeInfo)) {
      return [...transaction.modifiers];
    }
  } else if (isCodeChange === true && isCodeChangeType(searchSelection)) {
    if (isCPTChange(searchSelection)) {
      return [{ ...transaction.procedure }];
    } else if (isTechChange(searchSelection)) {
      return [{ ...transaction.technical_code }];
    } else if (isICDChange(searchSelection)) {
      return [...transaction.diagnoses];
    } else if (isModifierChange(searchSelection)) {
      return [...transaction.modifiers];
    }
  }
};

const getBulkOpOriginalCodes = (
  selectionInfo: Accelerate.CodesAddCodeBench | Accelerate.CodeChange
): any[] => {
  const transaction = selectionInfo.context?.transaction ?? {};
  const isAddOperation = 'codesInfo' in selectionInfo;
  const isDeleteOperation = 'codes' in selectionInfo;
  let searchSelection;

  if (isAddOperation) {
    searchSelection = {
      codeInfo: selectionInfo?.codesInfo?.[0],
      context: selectionInfo.context
    };
  } else if (isDeleteOperation) {
    searchSelection = selectionInfo;
  }

  if (isAddOperation && isSelection(searchSelection)) {
    if (isProcedure(searchSelection.codeInfo)) {
      return [];
    } else if (isSelectionICD(searchSelection.codeInfo)) {
      return [...transaction.diagnoses];
    } else if (isSelectionModifier(searchSelection.codeInfo)) {
      return [...transaction.modifiers];
    }
  } else if (isDeleteOperation) {
    if (isCPTChange(searchSelection)) {
      return [{ ...transaction.procedure }];
    } else if (isTechChange(searchSelection)) {
      return [{ ...transaction.technical_code }];
    } else if (isICDChange(searchSelection)) {
      return [...transaction.diagnoses];
    } else if (isModifierChange(searchSelection)) {
      return [...transaction.modifiers];
    }
  }
};

export const resequence = <T>(codes: T[]) => {
  // when dxs and mods are add/updated/removed from the list,
  // the frontend informs the backend of the new sequence.
  const sequenced = codes?.map(
    (code: T, idx: number): T => {
      return {
        ...code,
        sequence_number: idx + 1
      };
    }
  );
  return sequenced;
};

const _buildTransactionUpdate = (
  action: any
): InvoicesAPI.TransactionUpdate => {
  const transactionGuid = action.context.transaction?.guid ?? null;

  if (transactionGuid === null) {
    throw new Error('Changes and transaction guids are required for updates.');
  }

  return {
    transactionGuid
  };
};

const _buildTransactionCreate = (
  action: any
): InvoicesAPI.TransactionCreate => {
  const invoiceGuid = action.context.invoice?.guid ?? null;

  if (invoiceGuid === null) {
    throw new Error('Invoice guid is required for transaction creation.');
  }

  return {
    invoiceGuid
  };
};

// -----------------------------
// PUBLIC API
// -----------------------------
const updateDTO = (
  codeChange: Accelerate.CodeChange,
  searchDrop: boolean = false
): InvoicesAPI.TransactionUpdate => {
  const update: InvoicesAPI.TransactionUpdate = _buildTransactionUpdate(
    codeChange
  );

  const codesForUpdate = getCodesForUpdate(codeChange);
  // updates have 4 variations
  // 1. change the units on CPTs
  // 2. change units on Technical charges
  // 3. reorder diagnosis
  // 4. reorder modifiers
  // WE ARE ONLY SUPPORTING 1 & 2 AT THE MOMENT.

  // 1. change the units on CPTs
  // 2. change units on Technical charges
  if (isProceduralUpdate(codeChange)) {
    const [firstCode] = codeChange.codes;
    const [original] = codesForUpdate;

    if (!firstCode || !original) {
      throw new Error('unable to update procedure');
    }
    const isTech = `${codeChange.codes[0].provenance}`.toUpperCase() === 'TECH';

    if (!isTech) {
      update.procedure = codeMaker.procedure({
        ...original,
        units: firstCode.units
      });
    } else {
      update.technicalCode = codeMaker.technical({
        ...original,
        units: firstCode.units
      });
    }
  } else if (isICDChange(codeChange)) {
    const diagnoses = getTransactionFromChange(codeChange).diagnoses ?? [];
    const updatedDx = [];

    codeChange.codes.forEach(code => {
      const dx = diagnoses.find(d => d.diagnosis_code === code.code);
      if (dx) {
        updatedDx.push(dx);
      } else if (searchDrop) {
        updatedDx.push({
          ...BASE_DX,
          diagnosis_code: `${code.code}`
        });
      }
    });

    update.diagnoses = resequence(updatedDx);
  } else if (isModifierChange(codeChange)) {
    const modifiers = getTransactionFromChange(codeChange).modifiers ?? [];
    const updatedModifiers = [];

    codeChange.codes.forEach(code => {
      const modifier = modifiers.find(d => d.modifier_code === code.code);
      if (modifier) {
        updatedModifiers.push(modifier);
      } else if (searchDrop) {
        updatedModifiers.push({
          ...BASE_MOD,
          modifier_code: `${code.code}`
        });
      }
    });
    update.modifiers = resequence(updatedModifiers);
  }

  return update;
};

const createDTO = (
  selection: Accelerate.CodeAddActionPayload
): InvoicesAPI.TransactionUpdate => {
  const { codeInfo, context } = selection;
  const createDTO: InvoicesAPI.TransactionUpdate = _buildTransactionUpdate({
    codeInfo,
    context
  });

  // creation takes two flavors
  // 1. single for cpts/tech
  // 2. multi for dx/mods
  if (isSelectionCPT(codeInfo)) {
    createDTO.procedure = codeMaker.procedure({
      procedure_code: codeInfo.code,
      custom_cpt: codeInfo?.customCpt ?? false,
      units: codeInfo?.units ?? 1.0,
      code_provenances: codeInfo?.codeProvenances || null
    });
  } else if (isSelectionTech(codeInfo)) {
    createDTO.technicalCode = codeMaker.technical({
      code: codeInfo.code,
      units: codeInfo?.units ?? 1.0
    });
  } else {
    const codesToAdd = getOriginalCodes(selection, true);
    if (isSelectionICD(codeInfo)) {
      const dx = codeMaker.icd({
        diagnosis_code: `${codeInfo.code}`.toUpperCase(),
        code_provenances: codeInfo?.codeProvenances || null
      });
      codesToAdd.push(dx);
      createDTO.diagnoses = resequence(
        codesToAdd
      ) as InvoicesAPI.DiagnosticCode[];
    } else if (isSelectionModifier(codeInfo)) {
      const modifier = codeMaker.modifier({
        modifier_code: `${codeInfo.code}`.toUpperCase(),
        modifier_type: codeInfo.modifierType,
        code_provenances: codeInfo?.codeProvenances || null
      });
      codesToAdd.push(modifier);
      createDTO.modifiers = resequence(codesToAdd) as InvoicesAPI.Modifier[];
    }
  }

  return createDTO;
};

const BulkcreateDTO = (
  selection: Accelerate.CodesAddCodeBench
): InvoicesAPI.TransactionUpdate => {
  const createDTO: InvoicesAPI.TransactionUpdate = _buildTransactionUpdate(
    selection
  );

  // creation takes two flavors
  // 1. single for cpts/tech
  // 2. multi for dx/mods
  const codesToAdd = getBulkOpOriginalCodes(selection);

  if (isSelectionICD(selection?.codesInfo?.[0])) {
    selection?.codesInfo?.forEach(codeInfo => {
      const dx = codeMaker.icd({
        diagnosis_code: `${codeInfo?.code}`.toUpperCase(),
        code_provenances: codeInfo?.codeProvenances || null
      });
      codesToAdd?.push(dx);
    });
    createDTO.diagnoses = resequence(
      codesToAdd
    ) as InvoicesAPI.DiagnosticCode[];
  } else if (isSelectionModifier(selection?.codesInfo?.[0])) {
    selection?.codesInfo?.forEach(codeInfo => {
      const modifier = codeMaker.modifier({
        modifier_code: `${codeInfo?.code}`.toUpperCase(),
        modifier_type: codeInfo?.modifierType,
        code_provenances: codeInfo?.codeProvenances || null
      });
      codesToAdd?.push(modifier);
    });
    createDTO.modifiers = resequence(codesToAdd) as InvoicesAPI.Modifier[];
  }
  return createDTO;
};

const createTransactionDTO = (
  selection: CMxCommonApp.CodeInfo
): InvoicesAPI.TransactionCreate => {
  const createDTO: InvoicesAPI.TransactionCreate = _buildTransactionCreate(
    selection
  );
  createDTO.procedure = codeMaker.procedure({
    procedure_code: selection.code,
    custom_cpt: selection?.customCpt ?? false,
    units: selection?.units ?? 1.0,
    code_provenances: selection?.codeProvenances || null
  });
  return createDTO;
};

const deleteDTO = (
  codeChange: Accelerate.CodeChange
): InvoicesAPI.TransactionUpdate => {
  const deleteDTO = _buildTransactionUpdate(codeChange);

  const isTech = `${codeChange.codes[0].provenance}`.toUpperCase() === 'TECH';
  const [codeToRemove] = codeChange.codes;

  if (isTech) {
    deleteDTO.technicalCode = codeMaker.technical(codeToRemove);
  } else if (isCPTChange(codeChange)) {
    let [codeToSubmit] = getOriginalCodes(codeChange, true);
    deleteDTO.procedure = codeMaker.procedure(codeToSubmit);
  } else {
    let codesToSubmit = getOriginalCodes(codeChange, true);
    const icdRemoval = isICDChange(codeChange) && isDxCodes(codesToSubmit);
    const modifierRemoval =
      isModifierChange(codeChange) && isModCodes(codesToSubmit);

    if (icdRemoval) {
      const diagnoses = (codesToSubmit as InvoicesAPI.DiagnosticCode[]).filter(
        code => code.diagnosis_code === codeToRemove.code
      );

      deleteDTO.diagnoses =
        diagnoses.length === 0
          ? [{} as InvoicesAPI.DiagnosticCode]
          : resequence(diagnoses);
    } else if (modifierRemoval) {
      const modifiers = (codesToSubmit as InvoicesAPI.Modifier[]).filter(
        code => code.modifier_code === codeToRemove.code
      );

      deleteDTO.modifiers =
        modifiers.length === 0
          ? [{} as InvoicesAPI.Modifier]
          : resequence(modifiers);
    }
  }
  return deleteDTO;
};

const BulkDeleteDTO = (
  codeChange: Accelerate.CodeChange
): InvoicesAPI.TransactionUpdate => {
  const deleteDTO = _buildTransactionUpdate(codeChange);

  const [firstCode] = codeChange.codes;

  let codesToSubmit = getBulkOpOriginalCodes(codeChange);
  const icdRemoval = isICDChange(codeChange) && isDxCodes(codesToSubmit);
  const modifierRemoval =
    isModifierChange(codeChange) && isModCodes(codesToSubmit);

  if (icdRemoval) {
    const diagnoses = (codesToSubmit as InvoicesAPI.DiagnosticCode[]).filter(
      code =>
        codeChange?.codes?.some(
          removeCode => removeCode.code === code.diagnosis_code
        )
    );

    deleteDTO.diagnoses =
      diagnoses.length === 0
        ? [{} as InvoicesAPI.DiagnosticCode]
        : resequence(diagnoses);
  } else if (modifierRemoval) {
    const modifiers = (codesToSubmit as InvoicesAPI.Modifier[]).filter(code =>
      codeChange?.codes?.some(
        removeCode => removeCode.code === code.modifier_code
      )
    );

    deleteDTO.modifiers =
      modifiers.length === 0
        ? [{} as InvoicesAPI.Modifier]
        : resequence(modifiers);
  }
  return deleteDTO;
};

export {
  updateDTO,
  createDTO,
  deleteDTO,
  codePredicates,
  createTransactionDTO,
  BulkcreateDTO,
  BulkDeleteDTO
};
